import { authFetch } from "@/lib/authFetch";
import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  UserPlus,
  Menu,
  ArrowLeft,
  Upload,
  X,
  FileText,
  Clock,
  Building2,
  Users,
  Shield,
  Home,
  MapPin,
} from "lucide-react";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import Sidebar from "@/components/Sidebar";
import { LenderSelectItems } from "@/constants/lenders";
import { BH_LOCATION_OPTIONS, CFI_OFFICE_OPTIONS } from "@/constants/locations";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;



// US Timezones
const TIMEZONES = [
  { value: "America/New_York", label: "Eastern Time (ET)" },
  { value: "America/Chicago", label: "Central Time (CT)" },
  { value: "America/Denver", label: "Mountain Time (MT)" },
  { value: "America/Phoenix", label: "Arizona Time (AZ)" },
  { value: "America/Los_Angeles", label: "Pacific Time (PT)" },
  { value: "America/Anchorage", label: "Alaska Time (AK)" },
  { value: "Pacific/Honolulu", label: "Hawaii Time (HI)" },
];


const MAX_FILE_SIZE_MB = 50;

const filterOversizedFiles = (fileList) => {
  const accepted = [];
  for (const f of fileList) {
    if (f.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      toast.error(`"${f.name}" is too large (${(f.size / 1024 / 1024).toFixed(1)}MB). Maximum file size is ${MAX_FILE_SIZE_MB}MB.`, { duration: 6000 });
    } else {
      accepted.push(f);
    }
  }
  return accepted;
};

// Document Upload Component
const DocumentUpload = ({ files, onFilesChange, isDarkMode }) => {
  const fileInputRef = useRef(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(false);
    const droppedFiles = filterOversizedFiles(Array.from(e.dataTransfer.files));
    onFilesChange([...files, ...droppedFiles]);
  }, [files, onFilesChange]);

  const handleFileSelect = (e) => {
    const selectedFiles = filterOversizedFiles(Array.from(e.target.files));
    onFilesChange([...files, ...selectedFiles]);
  };

  const removeFile = (index) => {
    const newFiles = files.filter((_, i) => i !== index);
    onFilesChange(newFiles);
  };

  return (
    <div className="space-y-3">
      <div
        className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-all
          ${isDragOver ? "border-[#84c77f] bg-[#84c77f]/10" : isDarkMode ? "border-[#22c55e] hover:border-[#84c77f]" : "border-gray-300 hover:border-[#357c2a]"}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          onChange={handleFileSelect}
          className="hidden"
          accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.gif"
        />
        <Upload className={`h-8 w-8 mx-auto mb-2 ${isDarkMode ? "text-[#84c77f]" : "text-gray-400"}`} />
        <p className={`text-sm font-medium ${isDarkMode ? "text-slate-300" : "text-gray-700"}`}>
          OPTIONAL: If Loan Officer, Please Upload Credit Report or Any File Type
        </p>
        <p className={`text-xs mt-1 ${isDarkMode ? "text-slate-500" : "text-gray-500"}`}>
          Drag & drop or click to upload • Multiple Files Allowed
        </p>
      </div>
      
      {files.length > 0 && (
        <div className="space-y-2">
          {files.map((file, index) => (
            <div
              key={index}
              className={`flex items-center justify-between p-2 rounded-lg ${isDarkMode ? "bg-[#1a3d18]" : "bg-gray-50"}`}
            >
              <div className="flex items-center gap-2">
                <FileText className={`h-4 w-4 ${isDarkMode ? "text-[#84c77f]" : "text-gray-500"}`} />
                <span className={`text-sm truncate max-w-[200px] ${isDarkMode ? "text-slate-300" : "text-gray-700"}`}>
                  {file.name}
                </span>
                <span className={`text-xs ${isDarkMode ? "text-slate-500" : "text-gray-400"}`}>
                  ({(file.size / 1024).toFixed(1)} KB)
                </span>
              </div>
              <button
                type="button"
                onClick={() => removeFile(index)}
                className="p-1 hover:bg-red-100 rounded text-red-500"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const AddClientPage = ({ isAdmin = true }) => {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const { user } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [dupResolution, setDupResolution] = useState(null);
  const [dupSubmitting, setDupSubmitting] = useState(false);
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [requestType, setRequestType] = useState("co_referral");
  const [requestReason, setRequestReason] = useState("");
  const [affiliates, setAffiliates] = useState([]);
  const [counselors, setCounselors] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  
  // BH Quick Add Modal state
  const [showBHModal, setShowBHModal] = useState(false);
  const [bhSubmitting, setBHSubmitting] = useState(false);
  const [betenboughReps, setBetenboughReps] = useState([]);
  const [bhFiles, setBHFiles] = useState([]);
  const bhFileInputRef = useRef(null);
  const [apiLenders, setApiLenders] = useState([]);
  const [apiLendersByLocation, setApiLendersByLocation] = useState({});
  const [bhFormData, setBHFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    betenbough_location: "",
    betenbough_sales_rep: "",
    betenbough_affiliated_lender: "",
    betenbough_client_id_number: "",
    betenbough_rep_notes: "",
    preferred_lender_notes: "",
    source: "bh_dashboard",
    corporate_manager: "Lesa",
    has_joint_client: false,
    joint_first_name: "",
    joint_last_name: "",
    joint_email: "",
    joint_phone: "",
    // Status checkboxes
    bh_home_tour_completed: false,
    loan_officer_introduction: false,
    loan_officer_meeting_completed: false,
    loan_officer_prequal_pending_credit: false,
    under_agreement: false,
  });
  
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    timezone: "America/Chicago",
    office_location: "",
    // Affiliate info
    betenbough_sales_rep: "",
    betenbough_affiliate_2: "",
    betenbough_special_notes_or_credit_strategy: "",
    // Joint applicant
    has_joint: false,
    joint_first_name: "",
    joint_last_name: "",
    joint_email: "",
    joint_phone: "",
    joint_dob: "",
    // Internal Use Only
    internal_team_spanish: false,
    internal_team_new_lead: false,
    internal_team_special_affiliate: false,
    internal_team_direct_counselor: false,
    betenbough_assigned_credit_counselor: "",
    corporate_manager: "",
  });

  useEffect(() => {
    fetchAffiliates();
    fetchCounselors();
    fetchBetenboughReps();
    fetchPreferredLenders();
    // Auto-fill Affiliate 1 for non-admin partners
    if (!isAdmin && user?.name) {
      setFormData(prev => ({ ...prev, betenbough_sales_rep: prev.betenbough_sales_rep || user.name }));
      setBHFormData(prev => ({ ...prev, betenbough_sales_rep: prev.betenbough_sales_rep || user.name }));
    }
  }, []);

  const fetchAffiliates = async () => {
    try {
      const response = await authFetch(`${API}/lookup/affiliates`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setAffiliates(data.affiliates || []);
      }
    } catch (error) {
      console.error("Error fetching affiliates:", error);
    }
  };

  const fetchCounselors = async () => {
    try {
      const response = await authFetch(`${API}/lookup/counselors`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setCounselors(data.counselors || []);
      }
    } catch (error) {
      console.error("Error fetching counselors:", error);
    }
  };

  const fetchBetenboughReps = async () => {
    try {
      const response = await authFetch(`${API}/lookup/betenbough-reps`);
      if (response.ok) {
        const data = await response.json();
        setBetenboughReps(data.reps || []);
      }
    } catch (error) {
      console.error("Error fetching Betenbough reps:", error);
    }
  };

  const fetchPreferredLenders = async () => {
    try {
      const response = await authFetch(`${API}/lookup/preferred-lenders`);
      if (response.ok) {
        const data = await response.json();
        setApiLenders(data.lenders || []);
        setApiLendersByLocation(data.by_location || {});
      }
    } catch (error) {
      console.error("Error fetching preferred lenders:", error);
    }
  };

  const handleBHFormChange = (field, value) => {
    setBHFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleBHFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setBHFiles(prev => [...prev, ...selectedFiles]);
  };

  const removeBHFile = (index) => {
    setBHFiles(prev => prev.filter((_, i) => i !== index));
  };

  const resetBHForm = () => {
    setBHFormData({
      first_name: "",
      last_name: "",
      email: "",
      phone: "",
      betenbough_location: "",
      betenbough_sales_rep: "",
      betenbough_affiliated_lender: "",
      betenbough_client_id_number: "",
      betenbough_rep_notes: "",
      preferred_lender_notes: "",
      has_joint_client: false,
      joint_first_name: "",
      joint_last_name: "",
      joint_email: "",
      joint_phone: "",
      bh_home_tour_completed: false,
      loan_officer_introduction: false,
      loan_officer_meeting_completed: false,
      loan_officer_prequal_pending_credit: false,
      under_agreement: false,
    });
    setBHFiles([]);
  };

  const handleBHSubmit = async () => {
    if (!bhFormData.first_name || !bhFormData.last_name) {
      toast.error("First name and last name are required");
      return;
    }
    if (!bhFormData.betenbough_location) {
      toast.error("Please select a Betenbough location");
      return;
    }
    if (!bhFormData.betenbough_sales_rep) {
      toast.error("Please select a Betenbough rep");
      return;
    }

    setBHSubmitting(true);
    try {
      const formDataToSend = new FormData();
      formDataToSend.append("data", JSON.stringify(bhFormData));
      
      // Append files
      bhFiles.forEach((file) => {
        formDataToSend.append("files", file);
      });

      const response = await authFetch(`${API}/referrals/submit-with-files`, {
        method: "POST",
        body: formDataToSend,
      });

      const resultText = await response.text().catch(() => "");
      let result = null;
      try { result = JSON.parse(resultText); } catch {}

      if (response.ok || (result && result.success)) {
        toast.success("BH Client submitted successfully! Syncing to CRM...", { duration: 4000 });
        setShowBHModal(false);
        resetBHForm();
        navigate("/clients");
      } else if (response.status === 401) {
        toast.error("Your session has expired. Please log in again.");
        navigate("/login");
      } else {
        console.error(`BH submit failed: HTTP ${response.status}`, resultText);
        const detail = result?.detail;
        const errorMsg = typeof detail === "string" ? detail
          : Array.isArray(detail) ? detail.map(e => e.msg || JSON.stringify(e)).join("; ")
          : `Server error (${response.status}). Please try again.`;
        toast.error(errorMsg, { duration: 6000 });
      }
    } catch (error) {
      console.error("Error submitting BH client:", error);
      toast.error(`Connection error: ${error.message || "please check your internet and try again."}`, { duration: 6000 });
    } finally {
      setBHSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.first_name || !formData.last_name) {
      toast.error("First name and last name are required");
      return;
    }

    setSubmitting(true);
    try {
      // Build internal use only string - default to "New Lead Team" if nothing selected
      const internalUseTeams = [];
      if (formData.internal_team_new_lead) internalUseTeams.push("New Lead Team");
      if (formData.internal_team_spanish) internalUseTeams.push("Spanish Team");
      if (formData.internal_team_special_affiliate) internalUseTeams.push("Special Affiliate Lead Team");
      if (formData.internal_team_direct_counselor) internalUseTeams.push("Direct to Counselor");

      const submitData = {
        ...formData,
        internal_use_only: internalUseTeams.length > 0 ? internalUseTeams.join(", ") : "New Lead Team",
        secondary_type: formData.has_joint ? "Joint" : "Single",
        has_joint_client: formData.has_joint,
        lead_status_amp_stages: "New Lead",
        corporate_manager: formData.corporate_manager,
        assigned_counselor: formData.betenbough_assigned_credit_counselor,
      };

      // Handle file uploads if any
      if (uploadedFiles.length > 0) {
        const fileDataArray = await Promise.all(
          uploadedFiles.map(async (file) => {
            return new Promise((resolve) => {
              const reader = new FileReader();
              reader.onload = () => {
                resolve({
                  name: file.name,
                  type: file.type,
                  data: reader.result,
                });
              };
              reader.readAsDataURL(file);
            });
          })
        );
        submitData.uploaded_documents = fileDataArray;
      }

      const response = await authFetch(`${API}/clients/create`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(submitData),
      });

      const responseText = await response.text().catch(() => "");
      let data = null;
      try { data = JSON.parse(responseText); } catch {}

      if (response.ok || (data && data.success)) {
        toast.success("Client created successfully! Syncing to CRM...", { duration: 4000 });
        navigate(`${isAdmin ? "/admin" : "/team"}/clients/${data.client_id}`);
      } else if (response.status === 401) {
        toast.error("Your session has expired. Please log in again.");
        navigate("/login");
      } else {
        console.error(`Create client failed: HTTP ${response.status}`, responseText);
        const detail = data?.detail;
        if (detail && typeof detail === "object" && detail.code === "DUPLICATE_CLIENT") {
          setShowRequestForm(false);
          setRequestReason("");
          setRequestType("co_referral");
          setDupResolution(detail);
        } else {
          const errorMsg = typeof detail === "string" ? detail
            : Array.isArray(detail) ? detail.map(e => e.msg || JSON.stringify(e)).join("; ")
            : `Server error (${response.status}). Please try again.`;
          toast.error(errorMsg, { duration: 6000 });
        }
      }
    } catch (error) {
      console.error("Error creating client:", error);
      toast.error(`Connection error: ${error.message || "please check your internet and try again."}`, { duration: 6000 });
    } finally {
      setSubmitting(false);
    }
  };

  const basePath = isAdmin ? "/admin" : "/team";

  const dupViewClient = () => {
    if (!dupResolution?.client_id) return;
    navigate(`${basePath}/clients/${dupResolution.client_id}`);
  };

  const handleClaim = async () => {
    if (!dupResolution?.client_id) return;
    setDupSubmitting(true);
    try {
      const resp = await authFetch(`${API}/clients/${dupResolution.client_id}/claim`, {
        method: "POST", credentials: "include",
      });
      const data = await resp.json().catch(() => ({}));
      if (resp.ok && data.success) {
        toast.success("Client claimed — they're now in your pipeline.", { duration: 4000 });
        navigate(`${basePath}/clients/${dupResolution.client_id}`);
      } else {
        toast.error(typeof data.detail === "string" ? data.detail : "Could not claim this client.", { duration: 6000 });
      }
    } catch (e) {
      toast.error("Connection error. Please try again.");
    } finally {
      setDupSubmitting(false);
    }
  };

  const handleRequestAccess = async () => {
    if (!dupResolution?.client_id) return;
    setDupSubmitting(true);
    try {
      const resp = await authFetch(`${API}/clients/${dupResolution.client_id}/access-requests`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ request_type: requestType, reason: requestReason }),
      });
      const data = await resp.json().catch(() => ({}));
      if (resp.ok && data.success) {
        toast.success(data.message || "Request submitted. An admin will review it.", { duration: 5000 });
        setDupResolution(null);
      } else {
        toast.error(typeof data.detail === "string" ? data.detail : "Could not submit request.", { duration: 6000 });
      }
    } catch (e) {
      toast.error("Connection error. Please try again.");
    } finally {
      setDupSubmitting(false);
    }
  };

  return (
    <div className={`min-h-screen flex transition-colors duration-300 ${isDarkMode ? 'bg-black' : 'bg-slate-50'}`}>
      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        isAdmin={isAdmin}
      />

      <div className="flex-1 flex flex-col min-h-screen overflow-hidden md:ml-64">
        <header className={`border-b px-4 md:px-6 py-4 transition-colors duration-300 ${isDarkMode ? 'bg-black border-[#1a3d18]' : 'bg-white border-slate-200'}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setSidebarOpen(true)}
                className={`md:hidden p-2 rounded-lg transition-colors ${isDarkMode ? 'hover:bg-[#22c55e]/20 text-white' : 'hover:bg-slate-100'}`}
              >
                <Menu className="h-5 w-5" />
              </button>
              <div className="flex items-center gap-2">
                <Link to={`${basePath}/clients`}>
                  <Button variant="ghost" size="sm" className={isDarkMode ? 'text-slate-300 hover:text-white hover:bg-[#22c55e]/20' : ''}>
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Back to Clients
                  </Button>
                </Link>
              </div>
            </div>
            <h1 className={`text-xl font-semibold transition-colors ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Add New Client</h1>
          </div>
        </header>

        <main className="flex-1 p-4 md:p-6 overflow-y-auto">
          {/* Client Type Selection */}
          <div className="max-w-4xl mx-auto mb-4">
            <div className={`flex gap-2 p-1 rounded-lg ${isDarkMode ? 'bg-[#1a3d18]' : 'bg-slate-100'}`}>
              <Button
                type="button"
                variant="ghost"
                className={`flex-1 ${isDarkMode ? 'bg-[#22c55e] text-white hover:bg-[#22c55e]/80' : 'bg-white shadow-sm'}`}
                data-testid="cfi-client-tab"
              >
                <Building2 className="h-4 w-4 mr-2" />
                CFI Client
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={() => setShowBHModal(true)}
                className={`flex-1 ${isDarkMode ? 'text-slate-300 hover:bg-[#22c55e]/20' : 'hover:bg-slate-50'}`}
                data-testid="bh-client-tab"
              >
                <Home className="h-4 w-4 mr-2" />
                BH Client
              </Button>
            </div>
          </div>

          <Card className={`max-w-4xl mx-auto transition-colors duration-300 ${isDarkMode ? 'bg-[#265324] border-[#1a3d18]' : ''}`}>
            <CardHeader>
              <CardTitle className={`flex items-center gap-2 ${isDarkMode ? 'text-white' : ''}`}>
                <UserPlus className="h-5 w-5 text-[#22c55e]" />
                Client Information
              </CardTitle>
              <CardDescription className={isDarkMode ? 'text-slate-400' : ''}>
                Enter the client&apos;s details below. Fields marked with * are required.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Section 1: Personal Information */}
                <div className="space-y-4">
                  <div className={`flex items-center gap-2 text-sm font-semibold border-b pb-2 ${isDarkMode ? 'text-slate-300 border-[#22c55e]' : 'text-slate-700'}`}>
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${isDarkMode ? 'bg-[#22c55e] text-[#1a3d18]' : 'bg-[#84c77f] text-[#265324]'}`}>1</div>
                    Personal Information
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="first_name" className={isDarkMode ? 'text-slate-300' : ''}>First Name *</Label>
                      <Input
                        id="first_name"
                        value={formData.first_name}
                        onChange={(e) => handleChange("first_name", e.target.value)}
                        placeholder="John"
                        className={`mt-1 ${isDarkMode ? 'bg-[#1a3d18] border-[#22c55e] text-white' : ''}`}
                        required
                        data-testid="first-name-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="last_name" className={isDarkMode ? 'text-slate-300' : ''}>Last Name *</Label>
                      <Input
                        id="last_name"
                        value={formData.last_name}
                        onChange={(e) => handleChange("last_name", e.target.value)}
                        placeholder="Doe"
                        className={`mt-1 ${isDarkMode ? 'bg-[#1a3d18] border-[#22c55e] text-white' : ''}`}
                        required
                        data-testid="last-name-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email" className={isDarkMode ? 'text-slate-300' : ''}>Email</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleChange("email", e.target.value)}
                        placeholder="john.doe@example.com"
                        className={`mt-1 ${isDarkMode ? 'bg-[#1a3d18] border-[#22c55e] text-white' : ''}`}
                        data-testid="email-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone" className={isDarkMode ? 'text-slate-300' : ''}>Phone</Label>
                      <Input
                        id="phone"
                        value={formData.phone}
                        onChange={(e) => handleChange("phone", e.target.value)}
                        placeholder="(555) 123-4567"
                        className={`mt-1 ${isDarkMode ? 'bg-[#1a3d18] border-[#22c55e] text-white' : ''}`}
                        data-testid="phone-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="timezone" className={isDarkMode ? 'text-slate-300' : ''}>
                        <Clock className="h-4 w-4 inline mr-1" />
                        Time Zone
                      </Label>
                      <Select
                        value={formData.timezone}
                        onValueChange={(v) => handleChange("timezone", v)}
                      >
                        <SelectTrigger className={`mt-1 ${isDarkMode ? 'bg-[#1a3d18] border-[#22c55e] text-white' : ''}`} data-testid="timezone-select">
                          <SelectValue placeholder="Select Time Zone" />
                        </SelectTrigger>
                        <SelectContent>
                          {TIMEZONES.map((tz) => (
                            <SelectItem key={tz.value} value={tz.value}>{tz.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="office_location" className={isDarkMode ? 'text-slate-300' : ''}>
                        <MapPin className="h-4 w-4 inline mr-1" />
                        Office Location
                      </Label>
                      <Select
                        value={formData.office_location}
                        onValueChange={(v) => handleChange("office_location", v)}
                      >
                        <SelectTrigger className={`mt-1 ${isDarkMode ? 'bg-[#1a3d18] border-[#22c55e] text-white' : ''}`} data-testid="office-location-select">
                          <SelectValue placeholder="Select Office Location" />
                        </SelectTrigger>
                        <SelectContent>
                          {CFI_OFFICE_OPTIONS.map((loc) => (
                            <SelectItem key={loc.value} value={loc.value}>{loc.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                {/* Section 2: Assignment Details */}
                <div className="space-y-4">
                  <div className={`flex items-center gap-2 text-sm font-semibold border-b pb-2 ${isDarkMode ? 'text-slate-300 border-[#22c55e]' : 'text-slate-700'}`}>
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${isDarkMode ? 'bg-[#265324] text-white' : 'bg-[#265324] text-white'}`}>2</div>
                    Assignment Details
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className={isDarkMode ? 'text-slate-300' : ''}>Affiliate 1 (Referral Partner)</Label>
                      <SearchableSelect
                        options={affiliates}
                        value={formData.betenbough_sales_rep}
                        onValueChange={(v) => handleChange("betenbough_sales_rep", v)}
                        placeholder="Search or select affiliate..."
                        className="mt-1"
                        data-testid="affiliate1-select"
                      />
                    </div>
                    <div>
                      <Label className={isDarkMode ? 'text-slate-300' : ''}>Affiliate 2 (Optional)</Label>
                      <SearchableSelect
                        options={affiliates}
                        value={formData.betenbough_affiliate_2}
                        onValueChange={(v) => handleChange("betenbough_affiliate_2", v)}
                        placeholder="Search or select affiliate..."
                        className="mt-1"
                        data-testid="affiliate2-select"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <Link 
                        to="/register" 
                        className={`text-xs flex items-center gap-1 ${isDarkMode ? 'text-[#84c77f] hover:text-[#9ed899]' : 'text-[#357c2a] hover:text-[#84c77f]'}`}
                      >
                        Can&apos;t find your name? Create a free account here
                      </Link>
                    </div>
                  </div>
                </div>

                {/* Section 3: Joint Applicant */}
                <div className="space-y-4">
                  <div className={`flex items-center gap-2 text-sm font-semibold border-b pb-2 ${isDarkMode ? 'text-slate-300 border-[#22c55e]' : 'text-slate-700'}`}>
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${isDarkMode ? 'bg-[#22c55e] text-[#1a3d18]' : 'bg-[#84c77f] text-[#265324]'}`}>3</div>
                    Joint Applicant
                  </div>
                  <div className="flex items-center gap-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        id="has_joint"
                        checked={formData.has_joint}
                        onCheckedChange={(c) => handleChange("has_joint", c)}
                        data-testid="has-joint-checkbox"
                      />
                      <Label htmlFor="has_joint" className={`text-sm cursor-pointer ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                        This is a joint application
                      </Label>
                    </label>
                  </div>
                  {formData.has_joint && (
                    <div className={`grid grid-cols-1 md:grid-cols-2 gap-4 pl-2 border-l-2 ${isDarkMode ? 'border-[#22c55e]' : 'border-[#84c77f]'}`}>
                      <div>
                        <Label className={isDarkMode ? 'text-slate-300' : ''}>Joint First Name *</Label>
                        <Input
                          value={formData.joint_first_name}
                          onChange={(e) => handleChange("joint_first_name", e.target.value)}
                          placeholder="Jane"
                          className={`mt-1 ${isDarkMode ? 'bg-[#1a3d18] border-[#22c55e] text-white' : ''}`}
                          required
                          data-testid="joint-first-name"
                        />
                      </div>
                      <div>
                        <Label className={isDarkMode ? 'text-slate-300' : ''}>Joint Last Name *</Label>
                        <Input
                          value={formData.joint_last_name}
                          onChange={(e) => handleChange("joint_last_name", e.target.value)}
                          placeholder="Doe"
                          className={`mt-1 ${isDarkMode ? 'bg-[#1a3d18] border-[#22c55e] text-white' : ''}`}
                          required
                          data-testid="joint-last-name"
                        />
                      </div>
                      <div>
                        <Label className={isDarkMode ? 'text-slate-300' : ''}>Joint Email</Label>
                        <Input
                          type="email"
                          value={formData.joint_email}
                          onChange={(e) => handleChange("joint_email", e.target.value)}
                          placeholder="jane.doe@example.com"
                          className={`mt-1 ${isDarkMode ? 'bg-[#1a3d18] border-[#22c55e] text-white' : ''}`}
                          data-testid="joint-email"
                        />
                      </div>
                      <div>
                        <Label className={isDarkMode ? 'text-slate-300' : ''}>Joint Phone</Label>
                        <Input
                          value={formData.joint_phone}
                          onChange={(e) => handleChange("joint_phone", e.target.value)}
                          placeholder="(555) 123-4567"
                          className={`mt-1 ${isDarkMode ? 'bg-[#1a3d18] border-[#22c55e] text-white' : ''}`}
                          data-testid="joint-phone"
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Section 4: Notes */}
                <div className="space-y-4">
                  <div className={`flex items-center gap-2 text-sm font-semibold border-b pb-2 ${isDarkMode ? 'text-slate-300 border-[#22c55e]' : 'text-slate-700'}`}>
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${isDarkMode ? 'bg-[#22c55e] text-[#1a3d18]' : 'bg-[#84c77f] text-[#265324]'}`}>4</div>
                    Notes
                  </div>
                  <div>
                    <Label htmlFor="notes" className={isDarkMode ? 'text-slate-300' : ''}>Special Notes</Label>
                    <Textarea
                      id="notes"
                      value={formData.betenbough_special_notes_or_credit_strategy}
                      onChange={(e) => handleChange("betenbough_special_notes_or_credit_strategy", e.target.value)}
                      placeholder="Enter any special notes or credit strategy details..."
                      rows={3}
                      className={`mt-1 ${isDarkMode ? 'bg-[#1a3d18] border-[#22c55e] text-white' : ''}`}
                      data-testid="notes-input"
                    />
                  </div>
                </div>

                {/* Section 5: Document Upload */}
                <div className="space-y-4">
                  <div className={`flex items-center gap-2 text-sm font-semibold border-b pb-2 ${isDarkMode ? 'text-slate-300 border-[#22c55e]' : 'text-slate-700'}`}>
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${isDarkMode ? 'bg-[#265324] text-white' : 'bg-[#357c2a] text-white'}`}>5</div>
                    Documents
                  </div>
                  <DocumentUpload
                    files={uploadedFiles}
                    onFilesChange={setUploadedFiles}
                    isDarkMode={isDarkMode}
                  />
                </div>

                {/* Section 6: Internal Use Only */}
                <div className="space-y-4">
                  <div className={`flex items-center gap-2 text-sm font-semibold border-b pb-2 ${isDarkMode ? 'text-amber-400 border-amber-500' : 'text-amber-700 border-amber-300'}`}>
                    <div className="w-6 h-6 bg-amber-500 rounded-full flex items-center justify-center text-xs text-white">6</div>
                    <Shield className="h-4 w-4" />
                    For Internal Use Only
                  </div>
                  
                  <div className={`p-4 rounded-lg space-y-4 ${isDarkMode ? 'bg-[#1a3d18]' : 'bg-amber-50'}`}>
                    {/* Team Assignment Checkboxes */}
                    <div>
                      <Label className={`mb-3 block ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                        <Users className="h-4 w-4 inline mr-2" />
                        Assign to Team
                      </Label>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="new_lead_team"
                            checked={formData.internal_team_new_lead}
                            onCheckedChange={(c) => handleChange("internal_team_new_lead", c)}
                            data-testid="new-lead-team-checkbox"
                          />
                          <Label htmlFor="new_lead_team" className={`text-sm cursor-pointer ${isDarkMode ? 'text-slate-300' : ''}`}>
                            New Lead Team
                          </Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="special_affiliate_team"
                            checked={formData.internal_team_special_affiliate}
                            onCheckedChange={(c) => handleChange("internal_team_special_affiliate", c)}
                            data-testid="special-affiliate-team-checkbox"
                          />
                          <Label htmlFor="special_affiliate_team" className={`text-sm cursor-pointer ${isDarkMode ? 'text-slate-300' : ''}`}>
                            Special Affiliate Lead Team
                          </Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="spanish_team"
                            checked={formData.internal_team_spanish}
                            onCheckedChange={(c) => handleChange("internal_team_spanish", c)}
                            data-testid="spanish-team-checkbox"
                          />
                          <Label htmlFor="spanish_team" className={`text-sm cursor-pointer ${isDarkMode ? 'text-slate-300' : ''}`}>
                            Spanish Team
                          </Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="direct_counselor"
                            checked={formData.internal_team_direct_counselor}
                            onCheckedChange={(c) => handleChange("internal_team_direct_counselor", c)}
                            data-testid="direct-counselor-checkbox"
                          />
                          <Label htmlFor="direct_counselor" className={`text-sm cursor-pointer ${isDarkMode ? 'text-slate-300' : ''}`}>
                            Direct to Counselor
                          </Label>
                        </div>
                      </div>
                      <p className={`text-xs mt-1 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>Defaults to "New Lead Team" if none selected</p>
                    </div>

                    {/* Counselor & Manager Dropdowns */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label className={isDarkMode ? 'text-slate-300' : 'text-slate-700'}>Credit Counselor</Label>
                        <Select
                          value={formData.betenbough_assigned_credit_counselor}
                          onValueChange={(v) => handleChange("betenbough_assigned_credit_counselor", v)}
                        >
                          <SelectTrigger className={`mt-1 ${isDarkMode ? 'bg-[#265324] border-[#22c55e] text-white' : ''}`} data-testid="counselor-select">
                            <SelectValue placeholder="Select Counselor" />
                          </SelectTrigger>
                          <SelectContent className="max-h-[300px]">
                            {counselors.length > 0 ? (
                              counselors.map((name, idx) => (
                                <SelectItem key={idx} value={name}>{name}</SelectItem>
                              ))
                            ) : (
                              <SelectItem value="none" disabled>Loading counselors...</SelectItem>
                            )}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className={isDarkMode ? 'text-slate-300' : 'text-slate-700'}>Manager</Label>
                        <Select
                          value={formData.corporate_manager}
                          onValueChange={(v) => handleChange("corporate_manager", v)}
                        >
                          <SelectTrigger className={`mt-1 ${isDarkMode ? 'bg-[#265324] border-[#22c55e] text-white' : ''}`} data-testid="manager-select">
                            <SelectValue placeholder="Select Manager" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Dennis">Dennis</SelectItem>
                            <SelectItem value="Lesa">Lesa</SelectItem>
                            <SelectItem value="Gage">Gage</SelectItem>
                            <SelectItem value="Javan">Javan</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Submit Button */}
                <div className="flex items-center justify-end gap-3 pt-4 border-t">
                  <Link to={`${basePath}/clients`}>
                    <Button type="button" variant="outline" className={isDarkMode ? 'border-slate-500 text-slate-300 hover:bg-[#1a3d18]' : ''}>
                      Cancel
                    </Button>
                  </Link>
                  <Button
                    type="submit"
                    disabled={submitting}
                    className="bg-[#357c2a] hover:bg-[#265324] text-white px-6"
                    data-testid="submit-client-btn"
                  >
                    {submitting ? "Creating..." : "Create Client"}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </main>
      </div>

      {/* BH Client Quick Add Modal */}
      <Dialog open={showBHModal} onOpenChange={setShowBHModal}>
        <DialogContent className={`max-w-2xl max-h-[90vh] overflow-y-auto ${isDarkMode ? 'bg-[#1a3d18] border-[#22c55e] text-white' : ''}`}>
          <DialogHeader>
            <DialogTitle className={`flex items-center gap-2 ${isDarkMode ? 'text-white' : ''}`}>
              <Home className="h-5 w-5 text-[#c3ca6d]" />
              BH Client Quick Add
            </DialogTitle>
            <DialogDescription className={isDarkMode ? 'text-slate-400' : ''}>
              Submit a new Betenbough client referral. This form matches the /refer landing page.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Section 1: Client Information */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-sm font-semibold text-[#1b4541]">
                <div className="w-6 h-6 bg-[#c3ca6d] rounded-full flex items-center justify-center text-xs text-[#1b4541]">1</div>
                <span className={isDarkMode ? 'text-white' : ''}>Client Information</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pl-8">
                <div>
                  <Label className={isDarkMode ? 'text-slate-300' : 'text-slate-700'}>First Name *</Label>
                  <Input
                    value={bhFormData.first_name}
                    onChange={(e) => handleBHFormChange("first_name", e.target.value)}
                    placeholder="First name"
                    className={`mt-1 ${isDarkMode ? 'bg-[#265324] border-[#22c55e] text-white' : ''}`}
                    data-testid="bh-first-name"
                  />
                </div>
                <div>
                  <Label className={isDarkMode ? 'text-slate-300' : 'text-slate-700'}>Last Name *</Label>
                  <Input
                    value={bhFormData.last_name}
                    onChange={(e) => handleBHFormChange("last_name", e.target.value)}
                    placeholder="Last name"
                    className={`mt-1 ${isDarkMode ? 'bg-[#265324] border-[#22c55e] text-white' : ''}`}
                    data-testid="bh-last-name"
                  />
                </div>
                <div>
                  <Label className={isDarkMode ? 'text-slate-300' : 'text-slate-700'}>Email</Label>
                  <Input
                    type="email"
                    value={bhFormData.email}
                    onChange={(e) => handleBHFormChange("email", e.target.value)}
                    placeholder="email@example.com"
                    className={`mt-1 ${isDarkMode ? 'bg-[#265324] border-[#22c55e] text-white' : ''}`}
                    data-testid="bh-email"
                  />
                </div>
                <div>
                  <Label className={isDarkMode ? 'text-slate-300' : 'text-slate-700'}>Phone</Label>
                  <Input
                    value={bhFormData.phone}
                    onChange={(e) => handleBHFormChange("phone", e.target.value)}
                    placeholder="(555) 123-4567"
                    className={`mt-1 ${isDarkMode ? 'bg-[#265324] border-[#22c55e] text-white' : ''}`}
                    data-testid="bh-phone"
                  />
                </div>
              </div>
            </div>

            {/* Section 2: Joint Client */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-sm font-semibold text-[#1b4541]">
                <div className="w-6 h-6 bg-[#c3ca6d] rounded-full flex items-center justify-center text-xs text-[#1b4541]">2</div>
                <span className={isDarkMode ? 'text-white' : ''}>Joint Client (Optional)</span>
              </div>
              <div className="pl-8">
                <div className="flex items-center space-x-2 mb-4">
                  <Checkbox
                    id="bh_has_joint"
                    checked={bhFormData.has_joint_client}
                    onCheckedChange={(checked) => handleBHFormChange("has_joint_client", checked)}
                  />
                  <Label htmlFor="bh_has_joint" className={isDarkMode ? 'text-slate-300' : 'text-slate-700'}>
                    This client has a joint applicant
                  </Label>
                </div>
                {bhFormData.has_joint_client && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className={isDarkMode ? 'text-slate-300' : 'text-slate-700'}>Joint First Name</Label>
                      <Input
                        value={bhFormData.joint_first_name}
                        onChange={(e) => handleBHFormChange("joint_first_name", e.target.value)}
                        className={`mt-1 ${isDarkMode ? 'bg-[#265324] border-[#22c55e] text-white' : ''}`}
                      />
                    </div>
                    <div>
                      <Label className={isDarkMode ? 'text-slate-300' : 'text-slate-700'}>Joint Last Name</Label>
                      <Input
                        value={bhFormData.joint_last_name}
                        onChange={(e) => handleBHFormChange("joint_last_name", e.target.value)}
                        className={`mt-1 ${isDarkMode ? 'bg-[#265324] border-[#22c55e] text-white' : ''}`}
                      />
                    </div>
                    <div>
                      <Label className={isDarkMode ? 'text-slate-300' : 'text-slate-700'}>Joint Email</Label>
                      <Input
                        type="email"
                        value={bhFormData.joint_email}
                        onChange={(e) => handleBHFormChange("joint_email", e.target.value)}
                        className={`mt-1 ${isDarkMode ? 'bg-[#265324] border-[#22c55e] text-white' : ''}`}
                      />
                    </div>
                    <div>
                      <Label className={isDarkMode ? 'text-slate-300' : 'text-slate-700'}>Joint Phone</Label>
                      <Input
                        value={bhFormData.joint_phone}
                        onChange={(e) => handleBHFormChange("joint_phone", e.target.value)}
                        className={`mt-1 ${isDarkMode ? 'bg-[#265324] border-[#22c55e] text-white' : ''}`}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Section 3: Betenbough Details */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-sm font-semibold text-[#1b4541]">
                <div className="w-6 h-6 bg-[#c3ca6d] rounded-full flex items-center justify-center text-xs text-[#1b4541]">3</div>
                <span className={isDarkMode ? 'text-white' : ''}>Betenbough Details</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pl-8">
                <div>
                  <Label className={isDarkMode ? 'text-slate-300' : 'text-slate-700'}>Betenbough Location *</Label>
                  <Select
                    value={bhFormData.betenbough_location}
                    onValueChange={(v) => handleBHFormChange("betenbough_location", v)}
                  >
                    <SelectTrigger className={`mt-1 ${isDarkMode ? 'bg-[#265324] border-[#22c55e] text-white' : ''}`}>
                      <SelectValue placeholder="Select Location" />
                    </SelectTrigger>
                    <SelectContent>
                      {BH_LOCATION_OPTIONS.map((loc) => (
                        <SelectItem key={loc.value} value={loc.value}>{loc.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className={isDarkMode ? 'text-slate-300' : 'text-slate-700'}>Betenbough Rep *</Label>
                  <Select
                    value={bhFormData.betenbough_sales_rep}
                    onValueChange={(v) => handleBHFormChange("betenbough_sales_rep", v)}
                  >
                    <SelectTrigger className={`mt-1 ${isDarkMode ? 'bg-[#265324] border-[#22c55e] text-white' : ''}`}>
                      <SelectValue placeholder="Select Betenbough Rep" />
                    </SelectTrigger>
                    <SelectContent className="max-h-[300px]">
                      {betenboughReps.length > 0 ? (
                        betenboughReps.map((rep, idx) => {
                          const repName = typeof rep === 'string' ? rep : rep.name;
                          const repLabel = typeof rep === 'string' ? rep : (rep.display_label || rep.name);
                          return (
                            <SelectItem key={idx} value={repName}>{repLabel}</SelectItem>
                          );
                        })
                      ) : (
                        <SelectItem value="none" disabled>Loading reps...</SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-2">
                  <Label className={isDarkMode ? 'text-slate-300' : 'text-slate-700'}>Preferred Lender</Label>
                  <Select
                    value={bhFormData.betenbough_affiliated_lender}
                    onValueChange={(v) => handleBHFormChange("betenbough_affiliated_lender", v)}
                  >
                    <SelectTrigger className={`mt-1 ${isDarkMode ? 'bg-[#265324] border-[#22c55e] text-white' : ''}`}>
                      <SelectValue placeholder="Select Preferred Lender (Optional)" />
                    </SelectTrigger>
                    <SelectContent className="max-h-[300px]">
                      <LenderSelectItems location={bhFormData.betenbough_location} apiByLocation={apiLendersByLocation} apiLenders={apiLenders} testIdPrefix="bh-quick-add-lender" />
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-2">
                  <Label className={isDarkMode ? 'text-slate-300' : 'text-slate-700'}>Client Card ID Number (Mandatory if BH Rep)</Label>
                  <Input
                    value={bhFormData.betenbough_client_id_number}
                    onChange={(e) => handleBHFormChange("betenbough_client_id_number", e.target.value)}
                    placeholder="Enter client card ID number"
                    className={`mt-1 ${isDarkMode ? 'bg-[#265324] border-[#22c55e] text-white' : ''}`}
                    data-testid="bh-client-card-id"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label className={isDarkMode ? 'text-slate-300' : 'text-slate-700'}>Betenbough Rep Notes</Label>
                  <Textarea
                    value={bhFormData.betenbough_rep_notes}
                    onChange={(e) => handleBHFormChange("betenbough_rep_notes", e.target.value)}
                    placeholder="Notes from the Betenbough rep..."
                    className={`mt-1 ${isDarkMode ? 'bg-[#265324] border-[#22c55e] text-white' : ''}`}
                    rows={3}
                    data-testid="bh-rep-notes"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label className={isDarkMode ? 'text-slate-300' : 'text-slate-700'}>Preferred Lender Notes</Label>
                  <Textarea
                    value={bhFormData.preferred_lender_notes}
                    onChange={(e) => handleBHFormChange("preferred_lender_notes", e.target.value)}
                    placeholder="Notes for the preferred lender..."
                    className={`mt-1 ${isDarkMode ? 'bg-[#265324] border-[#22c55e] text-white' : ''}`}
                    rows={3}
                    data-testid="bh-lender-notes"
                  />
                </div>
              </div>
            </div>

            {/* Section 4: Client Status */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-sm font-semibold text-[#1b4541]">
                <div className="w-6 h-6 bg-[#c3ca6d] rounded-full flex items-center justify-center text-xs text-[#1b4541]">4</div>
                <span className={isDarkMode ? 'text-white' : ''}>Client Status (Optional)</span>
              </div>
              <div className="pl-8 space-y-3">
                <div className="flex items-center space-x-3">
                  <Checkbox
                    id="bh_tour"
                    checked={bhFormData.bh_home_tour_completed}
                    onCheckedChange={(checked) => handleBHFormChange("bh_home_tour_completed", checked)}
                  />
                  <Label htmlFor="bh_tour" className={`cursor-pointer ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                    Betenbough Homes New Home Tour Completed
                  </Label>
                </div>
                <div className="flex items-center space-x-3">
                  <Checkbox
                    id="bh_intro"
                    checked={bhFormData.loan_officer_introduction}
                    onCheckedChange={(checked) => handleBHFormChange("loan_officer_introduction", checked)}
                  />
                  <Label htmlFor="bh_intro" className={`cursor-pointer ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                    Loan Officer Introduction
                  </Label>
                </div>
                <div className="flex items-center space-x-3">
                  <Checkbox
                    id="bh_meeting"
                    checked={bhFormData.loan_officer_meeting_completed}
                    onCheckedChange={(checked) => handleBHFormChange("loan_officer_meeting_completed", checked)}
                  />
                  <Label htmlFor="bh_meeting" className={`cursor-pointer ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                    Loan Officer Meeting Completed
                  </Label>
                </div>
                <div className="flex items-center space-x-3">
                  <Checkbox
                    id="bh_prequal"
                    checked={bhFormData.loan_officer_prequal_pending_credit}
                    onCheckedChange={(checked) => handleBHFormChange("loan_officer_prequal_pending_credit", checked)}
                  />
                  <Label htmlFor="bh_prequal" className={`cursor-pointer ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                    Loan Officer - Pre Qual Pending Credit Repair
                  </Label>
                </div>
                <div className="flex items-center space-x-3">
                  <Checkbox
                    id="bh_agreement"
                    checked={bhFormData.under_agreement}
                    onCheckedChange={(checked) => handleBHFormChange("under_agreement", checked)}
                  />
                  <Label htmlFor="bh_agreement" className={`cursor-pointer ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                    Under Agreement
                  </Label>
                </div>
              </div>
            </div>

            {/* Section 5: Document Upload */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-sm font-semibold text-[#1b4541]">
                <div className="w-6 h-6 bg-[#c3ca6d] rounded-full flex items-center justify-center text-xs text-[#1b4541]">5</div>
                <span className={isDarkMode ? 'text-white' : ''}>Documents (Optional)</span>
              </div>
              <div className="pl-8">
                <div
                  className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-all
                    ${isDarkMode ? 'border-[#22c55e] hover:border-[#84c77f]' : 'border-gray-300 hover:border-[#357c2a]'}`}
                  onClick={() => bhFileInputRef.current?.click()}
                >
                  <input
                    ref={bhFileInputRef}
                    type="file"
                    multiple
                    onChange={handleBHFileSelect}
                    className="hidden"
                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                  />
                  <Upload className={`h-6 w-6 mx-auto mb-2 ${isDarkMode ? 'text-[#84c77f]' : 'text-gray-400'}`} />
                  <p className={`text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                    Click to upload credit reports or documents
                  </p>
                  <p className={`text-xs ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>
                    PDF, JPG, PNG, DOC, DOCX
                  </p>
                </div>
                {bhFiles.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {bhFiles.map((file, index) => (
                      <div key={index} className={`flex items-center justify-between p-2 rounded ${isDarkMode ? 'bg-[#265324]' : 'bg-slate-50'}`}>
                        <div className="flex items-center gap-2">
                          <FileText className={`h-4 w-4 ${isDarkMode ? 'text-[#84c77f]' : 'text-slate-400'}`} />
                          <span className={`text-sm ${isDarkMode ? 'text-white' : ''}`}>{file.name}</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeBHFile(index)}
                          className="p-1 hover:bg-red-100 rounded text-red-500"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Submit Buttons */}
            <div className="flex items-center justify-end gap-3 pt-4 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowBHModal(false);
                  resetBHForm();
                }}
                className={isDarkMode ? 'border-slate-500 text-slate-300 hover:bg-[#265324]' : ''}
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleBHSubmit}
                disabled={bhSubmitting}
                className="bg-[#c3ca6d] hover:bg-[#b3ba5d] text-[#1b4541] px-6"
                data-testid="submit-bh-client-btn"
              >
                {bhSubmitting ? "Submitting..." : "Submit BH Client"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!dupResolution} onOpenChange={(o) => { if (!o) setDupResolution(null); }}>
        <DialogContent data-testid="duplicate-resolution-dialog">
          <DialogHeader>
            <DialogTitle>This client already exists</DialogTitle>
            <DialogDescription>{dupResolution?.message}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {dupResolution?.relationship === "owned_by_self" || dupResolution?.relationship === "admin" ? (
              <Button className="w-full" onClick={dupViewClient} data-testid="dup-view-btn">
                View client
              </Button>
            ) : dupResolution?.relationship === "unassigned" ? (
              <div className="space-y-2">
                <p className="text-sm text-slate-600">This client isn't linked to any affiliate yet. Claim them to add them to your pipeline.</p>
                <Button className="w-full" onClick={handleClaim} disabled={dupSubmitting} data-testid="dup-claim-btn">
                  {dupSubmitting ? "Claiming..." : "Claim this client"}
                </Button>
              </div>
            ) : dupResolution?.relationship === "owned_by_other" ? (
              !showRequestForm ? (
                <div className="space-y-2">
                  <p className="text-sm text-slate-600">This client is managed by another affiliate. You can request access and an admin will review it.</p>
                  <Button className="w-full" onClick={() => setShowRequestForm(true)} data-testid="dup-request-open-btn">
                    Request access
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  <div>
                    <Label className="text-sm">Request type</Label>
                    <div className="flex gap-4 mt-1">
                      <label className="flex items-center gap-2 text-sm cursor-pointer">
                        <input type="radio" name="reqtype" checked={requestType === "co_referral"} onChange={() => setRequestType("co_referral")} data-testid="dup-req-coreferral" />
                        Co-referral (share)
                      </label>
                      <label className="flex items-center gap-2 text-sm cursor-pointer">
                        <input type="radio" name="reqtype" checked={requestType === "transfer"} onChange={() => setRequestType("transfer")} data-testid="dup-req-transfer" />
                        Transfer to me
                      </label>
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm">Reason (optional)</Label>
                    <Textarea value={requestReason} onChange={(e) => setRequestReason(e.target.value)} placeholder="e.g. This client now works with me" className="mt-1" data-testid="dup-req-reason" />
                  </div>
                  <Button className="w-full" onClick={handleRequestAccess} disabled={dupSubmitting} data-testid="dup-request-submit-btn">
                    {dupSubmitting ? "Submitting..." : "Submit request"}
                  </Button>
                </div>
              )
            ) : null}
            <Button variant="outline" className="w-full" onClick={() => setDupResolution(null)} data-testid="dup-cancel-btn">
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AddClientPage;
